import { icons } from './icons.ts';

export interface SubNavItem {
  id: string;
  name?: string;
  label?: string;
  icon?: string;
  danger?: boolean;
}

export class SubNav extends HTMLElement {
  private _items: SubNavItem[] = [];
  private _activeId: string = '';
  private _title: string = '';

  constructor() {
    super();
  }

  set items(val: SubNavItem[]) {
    this._items = val;
    this.render();
  }

  set activeId(val: string) {
    this._activeId = val;
    this.render();
  }

  override set title(val: string) {
    this._title = val;
    this.render();
  }

  set data(config: { items: SubNavItem[], activeId: string, title: string }) {
    this._items = config.items;
    this._activeId = config.activeId;
    this._title = config.title;
    this.render();
  }

  private render() {
    this.innerHTML = `
      <style>
        .sub-nav-container {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px 0;
          position: relative;
          width: 100%;
          height: min-content;
        }

        .sub-nav-container::before {
          content: "";
          position: absolute;
          top: 0; left: -1px; right: 0; bottom: 0;
          border: 1px solid rgba(255, 255, 255, 0.2);
          background: rgba(255, 255, 255, 0.1);
          filter: url(#sketchy-subnav);
          pointer-events: none;
        }

        .sub-nav-item {
          padding: 12px 24px;
          cursor: pointer;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: var(--text-dim);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          transition: all 0.2s ease;
          position: relative;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .sub-nav-item:hover {
          color: var(--text-main);
          background: rgba(255,255,255,0.02);
        }
        
        .sub-nav-item.active {
          color: var(--accent);
          background: transparent;
        }

        .sub-nav-item.danger-item {
          color: var(--error, #ff4444);
        }

        .sub-nav-item.danger-item:hover {
          background: rgba(255, 68, 68, 0.05);
        }

        .sub-nav-item.danger-item.active {
          color: var(--error, #ff4444);
          background: transparent;
          text-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
        }
      </style>

      <svg style="position: absolute; width: 0; height: 0; pointer-events: none; opacity: 0;">
        <defs>
          <filter id="sketchy-subnav" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence type="turbulence" baseFrequency="0.04" numOctaves="3" seed="3" result="noise" />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="2" xChannelSelector="R" yChannelSelector="G" />
          </filter>
        </defs>
      </svg>

      <div class="sub-nav-container">
        ${this._items.map(item => `
          <div class="sub-nav-item ${this._activeId === item.id ? 'active' : ''} ${item.danger ? 'danger-item' : ''}" 
               data-id="${item.id}">
            ${item.icon ? `<app-icon name="${item.icon}" size="14"></app-icon>` : ''}
            <span>${(item.name || (item as any).label || '').toUpperCase()}</span>
          </div>
        `).join('')}
      </div>
    `;

    this.querySelectorAll('.sub-nav-item').forEach(el => {
      el.addEventListener('click', () => {
        const id = el.getAttribute('data-id');
        if (id) {
          this.dispatchEvent(new CustomEvent('nav-change', { detail: { id } }));
          this.dispatchEvent(new CustomEvent('nav-click', { detail: id }));
        }
      });
    });
  }
}

if (!customElements.get('app-sub-nav')) {
  customElements.define('app-sub-nav', SubNav);
}
